import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../context/OverlayContext';
import OpponentDetailsOverlay from '../components/OpponentDetailsOverlay'; // Import the overlay
import { handleAuthError } from '../utils/auth';

const NewMatchPage = () => {
  const navigate = useNavigate();
  const { showOverlay } = useOverlay();
  const [matchName, setMatchName] = useState('');
  const [matchDescription, setMatchDescription] = useState('');
  const [matchLocation, setMatchLocation] = useState('');
  
  // New states for opponent selection and overlay
  const [opponents, setOpponents] = useState([]);
  const [selectedOpponentId, setSelectedOpponentId] = useState('');
  const [showOpponentOverlay, setShowOpponentOverlay] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // For search functionality

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      handleAuthError(navigate);
      return null;
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  // Fetch opponents on component mount
  useEffect(() => {
    const fetchOpponents = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch('https://best3intennis.com/best3intennis-api/api/get_opponents.php', {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        });

        if (!response.ok) {
          const result = await response.json().catch(() => null);
          if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
            handleAuthError(navigate);
            return;
          }
          throw new Error(result?.message || 'Failed to fetch opponents.');
        }

        const result = await response.json();
        
        // Process opponents to ensure firstName and lastName are available
        const processedOpponents = (result || []).map(opponent => {
          if (opponent.name && !opponent.firstName && !opponent.lastName) {
            const nameParts = opponent.name.split(' ');
            return {
              ...opponent,
              firstName: nameParts[0] || '',
              lastName: nameParts.slice(1).join(' ') || '',
            };
          }
          return opponent;
        });
        setOpponents(processedOpponents);
      } catch (error) {
        console.error('Error fetching opponents:', error);
        if (error.message.includes('token')) {
          handleAuthError(navigate);
        } else {
          showOverlay(`Failed to load opponents: ${error.message}`, 'error');
        }
      }
    };
    fetchOpponents();
  }, [navigate, showOverlay]);

  const handleAddOpponent = () => {
    setShowOpponentOverlay(true);
  };

  const handleOpponentSave = async (newOpponentData) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const url = newOpponentData.id 
        ? 'https://best3intennis.com/best3intennis-api/api/update_opponent.php' 
        : 'https://best3intennis.com/best3intennis-api/api/add_opponent.php';
      const method = newOpponentData.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify(newOpponentData),
        credentials: 'include',
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || `Failed to ${newOpponentData.id ? 'update' : 'add'} opponent.`);
      }

      const result = await response.json();

      showOverlay(`Opponent ${newOpponentData.id ? 'updated' : 'added'} successfully!`, 'success');
      setShowOpponentOverlay(false);
      
      // Refresh opponents list and select the new/updated one
      const fetchOpponents = async () => {
        const res = await fetch('https://best3intennis.com/best3intennis-api/api/get_opponents.php', {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!res.ok) {
            const result = await res.json().catch(() => null);
            if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
                handleAuthError(navigate);
                return;
            }
        }
        const data = await res.json();
        if (res.ok) {
          setOpponents(data || []);
          if (!newOpponentData.id && result.opponentId) { // If adding a new opponent, select it
            setSelectedOpponentId(result.opponentId);
          } else if (newOpponentData.id) { // If updating, ensure it's still selected
            setSelectedOpponentId(newOpponentData.id);
          }
        }
      };
      fetchOpponents();

    } catch (error) {
      console.error('Error saving opponent:', error);
      if (error.message.includes('token')) {
        handleAuthError(navigate);
      } else {
        showOverlay(`Failed to save opponent: ${error.message}`, 'error');
      }
    }
  };

  const handleOpponentClose = () => {
    setShowOpponentOverlay(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedOpponentId) {
      showOverlay('Please select an opponent or add a new one.', 'error');
      return;
    }

    const matchData = {
      matchName,
      matchDescription,
      matchLocation,
      opponentId: selectedOpponentId, // Use selected opponent ID
    };

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch('https://best3intennis.com/best3intennis-api/api/create_match.php', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(matchData),
        credentials: 'include', // Send session cookie
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || 'Failed to create match.');
      }
      
      const result = await response.json();
      if (result.matchId) {
        navigate(`/match-tracker/${result.matchId}`);
      } else {
        throw new Error(result.message || 'Could not retrieve match ID.');
      }
    } catch (error) {
      console.error('Error creating match:', error);
      if (error.message.includes('token')) {
        handleAuthError(navigate);
      } else {
        showOverlay(`Failed to create match: ${error.message}`, 'error');
      }
    }
  };

  // Filter opponents based on search term
  const filteredOpponents = opponents.filter(opponent => 
    `${opponent.firstName} ${opponent.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto my-8">
      <h2 className="text-2xl font-semibold text-green-800 mb-6 text-center">Create New Match</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="matchName" className="block text-gray-700 text-sm font-bold mb-2">Match Name*</label>
          <input
            type="text"
            id="matchName"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
            value={matchName}
            onChange={(e) => setMatchName(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="matchDescription" className="block text-gray-700 text-sm font-bold mb-2">Match Description</label>
          <textarea
            id="matchDescription"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 h-24"
            value={matchDescription}
            onChange={(e) => setMatchDescription(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="matchLocation" className="block text-gray-700 text-sm font-bold mb-2">Match Location (Surface)</label>
          <input
            type="text"
            id="matchLocation"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
            value={matchLocation}
            onChange={(e) => setMatchLocation(e.target.value)}
          />
        </div>
        
        <hr className="my-8 border-gray-300" />

        <h3 className="text-xl font-semibold text-green-800 mb-4 text-center">Opponent Details</h3>
        
        <div className="flex items-end space-x-2">
          <div className="flex-grow">
            <label htmlFor="opponentSelect" className="block text-gray-700 text-sm font-bold mb-2">Select Opponent*</label>
            <input
              type="text"
              id="opponentSearch"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 mb-2"
              placeholder="Search opponents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              id="opponentSelect"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
              value={selectedOpponentId}
              onChange={(e) => setSelectedOpponentId(e.target.value)}
              required
            >
              <option value="">-- Select an Opponent --</option>
              {filteredOpponents.map((opponent) => (
                <option key={opponent.id} value={opponent.id}>
                  {opponent.firstName} {opponent.lastName}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleAddOpponent}
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-full text-2xl leading-none flex items-center justify-center"
            style={{ width: '40px', height: '40px' }} // Fixed size for a round button
            title="Add New Opponent"
          >
            +
          </button>
        </div>

        <button type="submit" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300 w-full">
          Start Match
        </button>
      </form>

      {showOpponentOverlay && (
        <OpponentDetailsOverlay 
          opponent={null} // Always null for adding new opponent from this page
          onClose={handleOpponentClose} 
          onSave={handleOpponentSave} 
        />
      )}
    </div>
  );
};

export default NewMatchPage;


